import { useEffect, useMemo, useState } from 'react';
import OrganisationAccountings from 'shared/db/services/OrganisationAccountings';
import { ExternalAccountingProviderType } from 'shared/types/Invoicing';

import { useDBSyncContext } from '@/context/DBSyncContext';
import { databaseValidation } from '@/services/databaseValidation';
import Logger from '@/services/logger';

import { useDatabase } from './useDatabase';

export function useAccountingProviderState({
    pauseWhileSyncing = true,
}: { pauseWhileSyncing?: boolean } = {}) {
    const db = useDatabase();
    const [accountingProvider, setAccountingProvider] =
        useState<ExternalAccountingProviderType | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const { isSyncInProgress } = useDBSyncContext();
    const isPaused = pauseWhileSyncing && isSyncInProgress;

    const database = useMemo(() => db.getDatabase(), [db]);

    const accountingsService = useMemo(
        () =>
            new OrganisationAccountings({
                database,
                logDBAction: Logger.logRecordActivity,
                validation: databaseValidation,
            }),
        [database],
    );
    useEffect(() => {
        if (isPaused) {
            return;
        }

        const subscription = accountingsService
            .get()
            .observeWithColumns(['accounting_provider'])
            .subscribe(([accounting]) => {
                setAccountingProvider(accounting?.accountingProvider || null);
                setIsLoaded(true);
            });

        return () => {
            subscription.unsubscribe();
        };
    }, [accountingsService, isPaused]);

    return { accountingProvider, isLoaded };
}

export function useAccountingProvider() {
    return useAccountingProviderState({ pauseWhileSyncing: false })
        .accountingProvider;
}
